import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import type {
  BasketBookingPackageDetails,
  BasketTransportLeg,
  BookingDraft,
  CheckoutTransportType,
  PriceSummaryMandatoryService,
  PriceSummaryPackage,
  ServiceOverview,
} from '@/scopes/checkout/domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Benefits } from '@sunwebgroup/uds-ui/benefits'
import { Divider } from '@sunwebgroup/uds-ui/divider'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { TimelineStepPosition, TimelineStepState, VerticalTimeline } from '@sunwebgroup/uds-ui/vertical-timeline'
import cn from 'classnames'
import * as React from 'react'
import { useState } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { STICKY_TRACK_ATTRIBUTE } from '@/scopes/checkout/components/Layout/useSidebarBottomOffset'
import { ServiceIcon } from '@/scopes/checkout/components/ServiceIcon'
import { useBookingDraftQuery, useCheckoutUsps } from '@/scopes/checkout/domain/hooks'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import styles from './Basket.module.css'

interface BasketProps {
  /** Hides the price summary section; the total price is still shown. */
  hidePriceSummary?: boolean
}

export function Basket({ hidePriceSummary = false }: BasketProps) {
  const sectionRef = useScrollWithPageWhileStuck()

  return (
    <>
      <div {...{ [STICKY_TRACK_ATTRIBUTE]: '' }} className={styles.stickyTrack}>
        <div ref={sectionRef} className={styles.checkoutSection} data-qa="checkout-basket">
          <DesktopBasket hidePriceSummary={hidePriceSummary} />
        </div>
      </div>
      <MobileBasket hidePriceSummary={hidePriceSummary} />
    </>
  )
}

const STICKY_TOP_PX = 8

/**
 * The section is sticky and as tall as the viewport, so once it sticks, its own content is scrolled
 * in lockstep with the page until the end of the content is reached. Both rounded corners stay visible.
 */
function useScrollWithPageWhileStuck() {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const element = ref.current
    const track = element?.parentElement
    if (!element || !track)
      return

    let userOffset = 0
    let lastAppliedScrollTop = element.scrollTop

    const getMaxScroll = () => Math.max(element.scrollHeight - element.clientHeight, 0)
    const getPageDrivenScroll = () => {
      const stuckDistance = STICKY_TOP_PX - track.getBoundingClientRect().top
      return Math.max(stuckDistance, 0)
    }

    const clampUserOffset = (offset: number, pageDriven: number) =>
      Math.min(Math.max(offset, -pageDriven), getMaxScroll() - pageDriven)

    // Dropping the offset beyond the edges lets a page scroll chained from the basket's top/bottom take effect immediately
    const syncScroll = () => {
      const pageDriven = getPageDrivenScroll()
      userOffset = clampUserOffset(userOffset + element.scrollTop - lastAppliedScrollTop, pageDriven)
      element.scrollTop = pageDriven + userOffset
      lastAppliedScrollTop = element.scrollTop
    }

    const captureUserScroll = () => {
      const delta = element.scrollTop - lastAppliedScrollTop
      if (delta === 0)
        return
      userOffset = clampUserOffset(userOffset + delta, getPageDrivenScroll())
      lastAppliedScrollTop = element.scrollTop
    }

    syncScroll()
    const resizeObserver = new ResizeObserver(syncScroll)
    resizeObserver.observe(element)
    element.addEventListener('scroll', captureUserScroll, { passive: true })
    window.addEventListener('scroll', syncScroll, { passive: true })
    window.addEventListener('resize', syncScroll)

    return () => {
      resizeObserver.disconnect()
      element.removeEventListener('scroll', captureUserScroll)
      window.removeEventListener('scroll', syncScroll)
      window.removeEventListener('resize', syncScroll)
    }
  }, [])

  return ref
}

function DesktopBasket({ hidePriceSummary }: BasketProps) {
  const { isLoading, data } = useBookingDraftQuery()
  if (isLoading)
    return ''
  const basketInformation = data?.basketInformation
  if (basketInformation && !basketInformation.accommodationName)
    return ''

  return (
    <>
      <div className={styles.pinnedHeader}>
        <BasketHeader basketInformation={basketInformation} />
        <Divider />
      </div>
      <div className={styles.content}>
        <BasketPackageAndServices
          bookingPackageDetails={basketInformation?.bookingPackageDetails}
          transportType={data?.transportInfo?.type}
          items={basketInformation?.items}
        />
        <BasketBenefits />
        {!hidePriceSummary && (
          <BasketPriceSummary
            priceSummary={basketInformation?.priceSummary}
            totalPrice={basketInformation?.totalPrice}
            isRebooking={data?.isRebooking}
            rebookingPriceBreakdown={basketInformation?.rebookingPriceBreakdown}
          />
        )}
      </div>
      {basketInformation?.totalPrice && (
        <div className={styles.totalPriceFooter}>
          <BasketTotalPrice
            isRebooking={data?.isRebooking}
            totalPrice={basketInformation?.totalPrice}
            avgPrice={basketInformation?.avgPrice}
            rebookingPriceBreakdown={basketInformation?.rebookingPriceBreakdown}
          />
        </div>
      )}
    </>
  )
}

function BasketMobileTotalBottom({ ref, totalPrice, isOpen, ...props }: {
  ref?: React.Ref<HTMLDivElement>
  totalPrice?: string
  isOpen: boolean
}) {
  const intl = useIntl()
  const ToggleBanner = isOpen ? Sheet.Close : Sheet.Trigger

  return (
    <ToggleBanner>
      <div
        ref={ref}
        {...props}
        role="button"
        className={cn(styles.mobileTotalBottom, isOpen && styles.mobileTotalBottomOnTop)}
      >
        <Typography variant="body-m-bold-tight">
          {intl.formatMessage({ id: 'checkout_steps_basket_total_price_text' })}
        </Typography>
        <div className={styles.mobileTotalBottomPrice}>
          <Typography variant="body-m-bold-tight">{totalPrice}</Typography>
          {isOpen ? <Icon glyph="ChevronDown" size={24} /> : <Icon glyph="ChevronUp" size={24} />}
        </div>
      </div>
    </ToggleBanner>
  )
}

function MobileBasket({ hidePriceSummary }: BasketProps) {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  const [isOpen, setIsOpen] = useState(false)

  if (isLoading)
    return ''
  const basketInformation = data?.basketInformation
  if (basketInformation && !basketInformation.accommodationName)
    return ''

  return (
    <Sheet.Root onOpenChange={setIsOpen}>
      <BasketMobileTotalBottom totalPrice={basketInformation?.totalPrice} isOpen={isOpen} />
      <Sheet.Body side="bottom">
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'checkout_steps_basket_mobile_header' })}
        />
        <Sheet.Content>
          <div className={styles.mobileContent}>
            <BasketHeader basketInformation={basketInformation} />
            <BasketPackageAndServices
              bookingPackageDetails={basketInformation?.bookingPackageDetails}
              transportType={data?.transportInfo?.type}
              items={basketInformation?.items}
            />
            <BasketBenefits />
            {!hidePriceSummary && (
              <BasketPriceSummary
                priceSummary={basketInformation?.priceSummary}
                totalPrice={basketInformation?.totalPrice}
                isRebooking={data?.isRebooking}
                rebookingPriceBreakdown={basketInformation?.rebookingPriceBreakdown}
              />
            )}
            {' '}
            <BasketTotalPrice
              isRebooking={data?.isRebooking}
              totalPrice={basketInformation?.totalPrice}
              avgPrice={basketInformation?.avgPrice}
              rebookingPriceBreakdown={basketInformation?.rebookingPriceBreakdown}
            />
          </div>
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )
}

function BasketHeader({ basketInformation }: { basketInformation?: BookingDraft['basketInformation'] }) {
  return (
    <div className={styles.header}>
      <div className={styles.basketImageWrapper}>
        <MediaServerLazyImage
          src={basketInformation?.imageUrl ?? ''}
          widths={[560, 940, 1200]}
          alt={basketInformation?.accommodationName}
          width={560}
          height={240}
          loading="lazy"
        />
      </div>
      <div className={styles.headingGroup}>
        <Typography className={styles.accommodationName} variant="display-h-regular">
          {basketInformation?.accommodationName}
        </Typography>
        <Typography className={styles.location} variant="body-s-regular-tight">
          {basketInformation?.location}
        </Typography>
      </div>
    </div>
  )
}

function getTransportIcon(
  transportType: CheckoutTransportType | undefined,
  direction: 'outbound' | 'inbound',
): IconGlyph {
  if (transportType === 'bus')
    return direction === 'outbound' ? 'BusRight' : 'BusLeft'
  return direction === 'outbound' ? 'AirplaneTakingOff' : 'AirplaneReturn'
}

function BasketPackageAndServices({
  bookingPackageDetails,
  transportType,
  items,
}: {
  bookingPackageDetails?: BasketBookingPackageDetails
  transportType?: CheckoutTransportType
  items?: ServiceOverview[]
}) {
  return (
    <div>
      <BasketPackageDetails bookingPackageDetails={bookingPackageDetails} transportType={transportType} />
      <BasketItemsList items={items} />
    </div>
  )
}

function BasketPackageDetails({
  bookingPackageDetails,
  transportType,
}: {
  bookingPackageDetails?: BasketBookingPackageDetails
  transportType?: CheckoutTransportType
}) {
  const intl = useIntl()
  if (!bookingPackageDetails)
    return null

  const { tripDates, partyComposition, room, mealPlan, outboundTransport, inboundTransport, transfer }
    = bookingPackageDetails
  const { startDate, endDate } = tripDates ?? {}

  return (
    <div className={styles.packageDetails}>
      {startDate && endDate && (
        <BasketPackageDetailsRow
          icon="Calendar"
          label={intl.formatMessage({ id: 'checkout_steps_basket_trip_dates_label' })}
        >
          {startDate.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' })}
          {' - '}
          {endDate.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short', year: 'numeric' })}
          {tripDates?.duration && ` (${tripDates.duration})`}
        </BasketPackageDetailsRow>
      )}
      {partyComposition && (
        <BasketPackageDetailsRow
          icon="Family"
          label={intl.formatMessage({ id: 'checkout_steps_basket_party_composition_label' })}
        >
          {partyComposition}
        </BasketPackageDetailsRow>
      )}
      {!!room?.length && (
        <BasketPackageDetailsRow icon="Bed" label={intl.formatMessage({ id: 'checkout_steps_basket_room_label' })}>
          {room.map((roomName, index) => (
            <div key={index}>{roomName}</div>
          ))}
        </BasketPackageDetailsRow>
      )}
      {!!mealPlan?.length && (
        <BasketPackageDetailsRow
          icon="ForkAndKnive"
          label={intl.formatMessage({ id: 'checkout_steps_basket_mealplan_label' })}
        >
          {mealPlan.join(', ')}
        </BasketPackageDetailsRow>
      )}
      {outboundTransport && transportType !== 'self_drive' && (
        <BasketPackageDetailsRow
          icon={getTransportIcon(transportType, 'outbound')}
          label={intl.formatMessage({ id: 'checkout_steps_basket_outbound_transport_label' })}
        >
          <BasketTransportTimeline transport={outboundTransport} />
        </BasketPackageDetailsRow>
      )}
      {inboundTransport && transportType !== 'self_drive' && (
        <BasketPackageDetailsRow
          icon={getTransportIcon(transportType, 'inbound')}
          label={intl.formatMessage({ id: 'checkout_steps_basket_inbound_transport_label' })}
        >
          <BasketTransportTimeline transport={inboundTransport} />
        </BasketPackageDetailsRow>
      )}
      {transfer && (
        <BasketPackageDetailsRow
          icon="BusFront"
          label={intl.formatMessage({ id: 'checkout_steps_basket_transfer_label' })}
        >
          {transfer}
        </BasketPackageDetailsRow>
      )}
    </div>
  )
}

function BasketPackageDetailsRow({
  icon,
  label,
  children,
}: React.PropsWithChildren<{ icon: IconGlyph, label: string }>) {
  return (
    <div>
      <ListItem className={styles.item} variant="transparent">
        <Icon glyph={icon} size={24} />
        <div className={styles.itemContent}>
          <Typography variant="body-s-regular-tight" className={styles.packageDetailsLabel}>
            {label}
          </Typography>
          <Typography variant="body-m-regular-tight" className={styles.packageDetailsValue} as="div">
            {children}
          </Typography>
        </div>
      </ListItem>
    </div>
  )
}

function formatTransportOverline(dateTime: Date, locale: string) {
  const time = dateTime.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  const shortDate = dateTime.toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return `${time} (${shortDate})`
}

function BasketTransportTimeline({ transport }: { transport: BasketTransportLeg }) {
  const intl = useIntl()
  const isFlightLeg = transport.airline !== undefined || transport.flightNumber !== undefined
  const flightTypeText
    = isFlightLeg && transport.isFlexible !== undefined
      ? intl.formatMessage({
          id: transport.isFlexible
            ? 'checkout_steps_basket_flexible_flight_text'
            : 'checkout_steps_basket_standard_flight_text',
        })
      : undefined
  const departureText = [transport.airline, transport.flightNumber, flightTypeText && `(${flightTypeText})`]
    .filter(Boolean)
    .join(' ')

  return (
    <VerticalTimeline.Wrapper className={styles.flightTimeline}>
      <VerticalTimeline.Step
        position={TimelineStepPosition.First}
        state={TimelineStepState.Neutral}
        title={transport.departureLocation}
        overline={formatTransportOverline(transport.departureDateTime, intl.locale)}
      >
        {departureText || transport.baggageLines?.length
          ? (
              <div className={styles.flightLegDetails}>
                {departureText && <Typography variant="body-m-regular-snug">{departureText}</Typography>}
                {transport.baggageLines?.map((line, index) => (
                  <Typography key={index} variant="body-m-regular-snug">
                    {line}
                  </Typography>
                ))}
              </div>
            )
          : undefined}
      </VerticalTimeline.Step>
      <VerticalTimeline.Step
        position={TimelineStepPosition.Last}
        state={TimelineStepState.Neutral}
        title={transport.arrivalLocation}
        overline={
          transport.arrivalDateTime ? formatTransportOverline(transport.arrivalDateTime, intl.locale) : undefined
        }
      />
    </VerticalTimeline.Wrapper>
  )
}

function BasketItemsList({ items }: { items?: ServiceOverview[] }) {
  if (!items?.length)
    return null

  return (
    <div>
      {items.map((item, index) => (
        <ListItem key={index} className={styles.item} variant="transparent">
          <ServiceIcon type={item.serviceAName || item.icon} size={24} />
          <div className={styles.itemContent}>
            <Typography variant="body-s-regular-tight" className={styles.itemHeaderText}>
              {item.title}
            </Typography>
            <div className={styles.itemBody}></div>

            {item.lines.map((line, lineIndex) => (
              <Typography
                key={lineIndex}
                variant={line.isTitle ? 'body-m-bold-tight' : 'body-m-regular-tight'}
                className={styles.itemLine}
              >
                {line.text}
              </Typography>
            ))}
          </div>
        </ListItem>
      ))}
    </div>
  )
}

function BasketBenefits() {
  const benefits = useCMSPageField('Checkout.benefits')?.items ?? []
  return (
    <>
      {benefits.map(({ componentId }) => (
        <BasketBenefitsItem key={componentId} componentId={componentId} />
      ))}
    </>
  )
}

function BasketBenefitsItem({ componentId }: { componentId: string }) {
  const { data, isLoading } = useCheckoutUsps(componentId)
  const usps = data?.usps
  if (isLoading || !usps)
    return null

  return (
    <Benefits.Root display="list" className={styles.benefits}>
      {usps.map((usp, index) => (
        <Benefits.Item key={index} iconClassName={styles.benefitsIcon} title={usp.text} supportingText={usp.subtitle} />
      ))}
    </Benefits.Root>
  )
}

const PACKAGE_SERVICE_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  accommodation: 'checkout_steps_basket_price_summary_service_type_accommodation',
  transport: 'checkout_steps_basket_price_summary_service_type_transport',
}

function getPackageServiceTypeTranslationKey(serviceType: string) {
  return PACKAGE_SERVICE_TYPE_TRANSLATION_KEYS[serviceType.toLowerCase()]
}

function BasketPriceSummaryPackageRow({ packageSummary }: { packageSummary: PriceSummaryPackage }) {
  const intl = useIntl()
  const { serviceTypes, mealPlan, totalPrice } = packageSummary

  const translatedServiceTypes = (serviceTypes ?? [])
    .map(getPackageServiceTypeTranslationKey)
    .filter((translationKey): translationKey is string => Boolean(translationKey))
    .map(translationKey => intl.formatMessage({ id: translationKey }))

  const supportingText = [...translatedServiceTypes, mealPlan].filter(Boolean).join(', ')

  if (totalPrice === undefined)
    return null

  return (
    <div className={styles.priceSummaryItem}>
      <div className={styles.priceSummaryRow}>
        <Typography variant="body-m-regular-tight">
          {intl.formatMessage({ id: 'checkout_steps_basket_price_summary_package_label' })}
        </Typography>
        <Typography variant="body-m-regular-tight">{intl.formatCurrency(totalPrice)}</Typography>
      </div>
      {supportingText && (
        <Typography variant="body-s-regular-tight" className={styles.priceSummarySupportingText}>
          {supportingText}
        </Typography>
      )}
    </div>
  )
}

function BasketPriceSummaryMandatoryServiceRow({
  mandatoryService,
}: {
  mandatoryService: PriceSummaryMandatoryService
}) {
  const intl = useIntl()
  const { name, totalPrice } = mandatoryService

  if (totalPrice === undefined)
    return null

  return (
    <div className={styles.priceSummaryRow}>
      <Typography variant="body-m-regular-tight">
        {name}
        {' '}
        (
        {intl.formatCurrency(totalPrice, { showSign: totalPrice < 0 })}
        )
      </Typography>
      <Typography variant="body-m-regular-tight" className={styles.priceSummaryMandatoryIncluded}>
        {intl.formatMessage({ id: 'checkout_steps_basket_price_summary_mandatory_included_label' })}
      </Typography>
    </div>
  )
}

function BasketPriceSummary({
  priceSummary,
  totalPrice,
  isRebooking,
  rebookingPriceBreakdown,
}: {
  priceSummary?: BookingDraft['basketInformation']['priceSummary']
  totalPrice?: string
  isRebooking?: boolean
  rebookingPriceBreakdown?: BookingDraft['basketInformation']['rebookingPriceBreakdown']
}) {
  const intl = useIntl()
  if (!priceSummary)
    return null

  const { package: packageSummary, mandatoryServices, extras, discount } = priceSummary
  // For rebookings, `totalPrice` is the amount still due after prior payments, which does not
  // match this breakdown's package/extras/discount figures. Use the trip's package total instead.
  const summaryTotalPrice = isRebooking ? rebookingPriceBreakdown?.totalPackagePrice : totalPrice

  return (
    <div className={styles.priceSummary}>
      {packageSummary && <BasketPriceSummaryPackageRow packageSummary={packageSummary} />}
      {mandatoryServices?.map((mandatoryService, index) => (
        <BasketPriceSummaryMandatoryServiceRow
          key={mandatoryService.serviceId ?? `mandatory-${index}`}
          mandatoryService={mandatoryService}
        />
      ))}
      {extras !== undefined && (
        <div className={styles.priceSummaryRow}>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({ id: 'checkout_steps_basket_price_summary_extras_label' })}
          </Typography>
          <Typography variant="body-m-regular-tight">
            {intl.formatCurrency(extras, { showSign: extras < 0 })}
          </Typography>
        </div>
      )}
      {discount !== undefined && discount !== 0 && (
        <div className={styles.priceSummaryRow}>
          <Typography variant="body-m-regular-tight" className={styles.priceSummaryDiscount}>
            {intl.formatMessage({ id: 'checkout_steps_basket_price_summary_discount_label' })}
          </Typography>
          <Typography variant="body-m-regular-tight" className={styles.priceSummaryDiscount}>
            {intl.formatCurrency(discount, { showSign: true })}
          </Typography>
        </div>
      )}
      {summaryTotalPrice && (
        <div className={cn(styles.priceSummaryRow, styles.priceSummaryTotal)}>
          <Typography variant="body-m-bold-tight">
            {intl.formatMessage({ id: 'checkout_steps_basket_price_summary_total_label' })}
          </Typography>
          <Typography variant="body-m-bold-tight">{summaryTotalPrice}</Typography>
        </div>
      )}
    </div>
  )
}

function BasketTotalPrice({
  isRebooking,
  totalPrice,
  avgPrice,
  rebookingPriceBreakdown,
}: {
  isRebooking?: boolean
  totalPrice?: string
  avgPrice?: string
  rebookingPriceBreakdown?: BookingDraft['basketInformation']['rebookingPriceBreakdown']
}) {
  const intl = useIntl()
  if (!totalPrice)
    return null

  if (isRebooking) {
    return (
      <>
        <div className={styles.totalPrice}>
          <div className={styles.totalPriceRow}>
            <Typography variant="body-m-regular-tight">
              {intl.formatMessage({ id: 'checkout_steps_basket_package_price_text' })}
            </Typography>
            <div className={styles.totalPriceRebookingPackagePrice}>
              <Typography variant="body-m-bold-tight">{rebookingPriceBreakdown?.totalPackagePrice}</Typography>
              {rebookingPriceBreakdown?.avgTotalPackagePrice && (
                <Typography variant="body-s-regular-tight" className={styles.totalPriceSubtext}>
                  {rebookingPriceBreakdown.avgTotalPackagePrice}
                </Typography>
              )}
            </div>
          </div>
          <div className={styles.totalPriceRow}>
            <Typography variant="body-m-regular-tight">
              {intl.formatMessage({ id: 'checkout_steps_basket_previous_booking_paid_text' })}
            </Typography>
            <Typography variant="body-m-bold-tight">{rebookingPriceBreakdown?.originalBookingPaidAmount}</Typography>
          </div>
          <div className={styles.totalPriceRow}>
            <Typography variant="display-g-regular">
              {intl.formatMessage({ id: 'checkout_steps_basket_amount_due_text' })}
            </Typography>
            <Typography variant="display-g-regular">{totalPrice}</Typography>
          </div>
        </div>
        {rebookingPriceBreakdown?.rebookingWarning && (
          <Banner size="M" color="success" className={styles.rebookingBanner}>
            <Typography variant="body-m-regular-tight">{rebookingPriceBreakdown.rebookingWarning}</Typography>
          </Banner>
        )}
      </>
    )
  }

  return (
    <div className={styles.totalPrice}>
      <div className={styles.totalPriceRow}>
        <Typography variant="display-g-regular">
          {intl.formatMessage({ id: 'checkout_steps_basket_total_price_text' })}
        </Typography>
        <Typography variant="display-g-regular">{totalPrice}</Typography>
      </div>
      {avgPrice && (
        <div className={cn(styles.totalPriceRow, styles.totalPriceSubtext)}>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({ id: 'checkout_steps_basket_average_price_text' })}
          </Typography>
          <Typography variant="body-m-bold-tight">{avgPrice}</Typography>
        </div>
      )}
    </div>
  )
}
